import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import { gsap } from "gsap";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Types
type DataPoint = { x: number; y: number };
type GraphLine = {
  _id: string;
  name: string; // "Actual" or "Projected"
  style: "solid" | "dotted" | "bar";
  color: string;
  dataPoints: DataPoint[];
};
type SubTab = {
  _id: string;
  title: string;
  xAxisTitle: string;
  yAxisTitle: string;
  graphLines: GraphLine[];
  image?: string;
};
type Tab = {
  _id: string;
  tabName: string;
  desc?: string;
  graphdetails: string[];
  subtabs: SubTab[];
};

const GraphUI: React.FC = () => {
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [activeTab, setActiveTab] = useState<Tab | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<SubTab | null>(null);

  // Fetch API
  const fetchTabs = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_LOCAL_URL}/api/graphData`);
      setTabs(res.data.data);

      if (res.data.data.length > 0) {
        const firstTab = res.data.data[0];
        setActiveTab(firstTab);
        if (firstTab.subtabs.length > 0) setActiveSubTab(firstTab.subtabs[0]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchTabs();
  }, []);

  // Tab click
  const handleTabClick = (tab: Tab) => {
    if (tab._id !== activeTab?._id) {
      gsap.fromTo(
        ".tab-content",
        { x: 50, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.5 }
      );
      setActiveTab(tab);
      setActiveSubTab(tab.subtabs[0] || null);
    }
  };

  // Subtab click
  const handleSubTabClick = (subtab: SubTab) => {
    if (subtab._id !== activeSubTab?._id) {
      gsap.fromTo(
        ".subtab-content",
        { x: 50, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.5 }
      );
      setActiveSubTab(subtab);
    }
  };

  // Line graph with joined Actual + Projected
  const renderLineGraph = (subtab: SubTab) => {
    const actualLine = subtab.graphLines.find((l) => l.name === "Actual");
    const projectedLine = subtab.graphLines.find((l) => l.name === "Projected");

    if (!actualLine && !projectedLine) return null;

    const allX = Array.from(
      new Set([
        ...(actualLine?.dataPoints.map((p) => p.x) || []),
        ...(projectedLine?.dataPoints.map((p) => p.x) || []),
      ])
    ).sort((a, b) => a - b);

    const mergedData = allX.map((x) => {
      const actualDP = actualLine?.dataPoints.find((p) => p.x === x);
      const projectedDP = projectedLine?.dataPoints.find((p) => p.x === x);
      return actualDP ? actualDP.y : projectedDP ? projectedDP.y : null;
    });

    const borderColor = actualLine?.color || "#fff";

    const data = {
      labels: allX,
      datasets: [
        {
          label: "",
          data: mergedData,
          borderColor: borderColor,
          backgroundColor: borderColor,
          borderWidth: 6, 
          pointBackgroundColor: "#ffffff", // white dots
          pointBorderColor: "#ffffff",
          pointRadius: 5,
          spanGaps: true,
          segment: {
            borderDash: (ctx: any) => {
              // Dotted for projected, solid for actual
              const index = ctx.p0DataIndex;
              return index >= (actualLine?.dataPoints.length || 0)
                ? [5, 5]
                : [];
            },
          },
        },
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: { display: false },
      },
      scales: {
        x: {
          title: { display: true, text: subtab.xAxisTitle, color: "#ffffff" },
          grid: {
            color: "#ffffff", // grid line color
            borderColor: "#ffffff", // axis line color
            borderDash: [4, 4], // dotted grid lines
          },
          ticks: {
            color: "#ffffff", // x-axis label color
          },
        },
        y: {
          title: { display: true, text: subtab.yAxisTitle, color: "#ffffff" },
          grid: {
            color: "#ffffff",
            borderColor: "#ffffff",
            borderDash: [4, 4],
          },
          ticks: {
            color: "#ffffff", // y-axis label color
          },
        },
      },
    };

    return <Line data={data} options={options} className="mb-6" />;
  };

  // Bar chart for cohort
  const renderBarGraph = (subtab: SubTab) => {
    return subtab.graphLines.map((line) => {
      const data = {
        labels: line.dataPoints.map((p) => p.x),
        datasets: [
          {
            label: line.name,
            data: line.dataPoints.map((p) => p.y),
            backgroundColor: line.color,
          },
        ],
      };

      const options = {
        responsive: true,
        plugins: {
          legend: {
            display: activeTab?.tabName !== "Cohort-wise job creation", // hide legend
            position: "top" as const,
          },
          datalabels: {
            color: "#ffffff",
            anchor: "end",
            align: "top",
            font: { weight: "bold" },
          },
        },

        scales: {
          x: {
            title: { display: true, text: subtab.xAxisTitle, color: "#ffffff" },
            grid: {
              color: "#ffffff",
              borderColor: "#ffffff",
              borderDash: [4, 4],
            },
            ticks: { color: "#ffffff" },
          },
          y: {
            title: { display: true, text: subtab.yAxisTitle, color: "#ffffff" },
            grid: {
              color: "#ffffff",
              borderColor: "#ffffff",
              borderDash: [4, 4],
            },
            ticks: { color: "#ffffff" },
          },
        },
      };

      return (
        <Bar key={line._id} data={data} options={options} className="mb-6" />
      );
    });
  };

  // Render graph dynamically
  const renderGraph = (subtab: SubTab) => {
    if (activeTab?.tabName === "Cohort-wise job creation")
      return renderBarGraph(subtab);
    else return renderLineGraph(subtab);
  };

  return (
    <section className="bg-teal blade-top-padding-lg blade-bottom-padding-lg">
      <div className="w-container-xl ">
        <h1 className="font-lato-bold text-white border-b border-white pb-3">
          Our impact
        </h1>

        <div className="flex flex-col lg:flex-row gap-6 ">
          {/* Tabs */}
          <div className="w-full border-r-2 border-white  lg:w-[25%] pr-4 flex flex-col gap-4 pt-8">
            {tabs.map((tab) => (
              <button
                key={tab._id}
                onClick={() => handleTabClick(tab)}
                className={`text-left font-lato-regular py-3 px-2 text-lg  w-full  ${
                  activeTab?._id === tab._id
                    ? "bg-pear text-black font-bold"
                    : "bg-transparent text-white"
                }`}
              >
                {tab.tabName}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 tab-content pt-8 mb-4">
            {activeTab && (
              <div>
                <h2 className="text-white font-bold text-3xl">
                  {activeTab.tabName}
                </h2>
                {activeTab.tabName !== "Cohort-wise job creation" && (
                  <div className="flex flex-col gap-3 my-6">
                    {activeTab.graphdetails?.map((detail, idx) => (
                      <div
                        key={idx}
                        className="flex flex-row gap-5 items-center"
                      >
                        {detail === "Actual" ? (
                          <>
                            <div className="bg-pear w-24 h-1"></div>
                            <h5 className="text-white font-lato-regular">
                              {detail}
                            </h5>
                          </>
                        ) : (
                          <>
                            <div className="border-b-2 border-pear border-dotted w-24 h-1"></div>
                            <h5 className="text-white font-lato-regular">
                              {detail}
                            </h5>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {activeTab.desc && (
                  <h3 className="font-lato-regular text-white py-3 ">
                    {activeTab.desc}
                  </h3>
                )}

                {/* Subtabs */}
                {activeTab.subtabs.length > 1 && (
                  <div className="flex gap-6 mb-8">
                    {activeTab.subtabs.map((subtab) => (
                      <button
                        key={subtab._id}
                        onClick={() => handleSubTabClick(subtab)}
                        className={`px-3 py-2 border-b-4 ${
                          activeSubTab?._id === subtab._id
                            ? "border-pear font-bold"
                            : "border-white"
                        } text-white`}
                      >
                        {subtab.title}
                      </button>
                    ))}
                  </div>
                )}

                {/* Graph */}
                {activeSubTab && (
                  <div className="subtab-content">
                    {renderGraph(activeSubTab)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GraphUI;
